import { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, MapPin } from 'lucide-react';

import { BrowserMapAdapter, isBrowserMapConfigured } from '../maps/browserMapAdapter';

export default function ConfirmedPinPicker({ value, onChange, error }) {
  const containerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const initialPositionRef = useRef(
    value?.latitude != null && value?.longitude != null
      && String(value.latitude).trim() !== '' && String(value.longitude).trim() !== ''
      && Number.isFinite(Number(value.latitude)) && Number.isFinite(Number(value.longitude))
      ? { lat: Number(value.latitude), lng: Number(value.longitude) }
      : null
  );
  const [state, setState] = useState(isBrowserMapConfigured() ? 'loading' : 'unavailable');

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!containerRef.current || !isBrowserMapConfigured()) return undefined;
    let disposed = false;
    let cleanup;
    const adapter = new BrowserMapAdapter();
    adapter.renderPinPicker(containerRef.current, {
      position: initialPositionRef.current,
      onSelect: (point) => onChangeRef.current?.({
        ...point,
        pinConfirmed: true,
        locationSource: 'MANUAL_PIN',
        locationQuality: 'CONFIRMED',
        geocodeProvider: 'GOOGLE_MAPS',
        providerPlaceId: null,
        accuracyM: null,
      }),
    }).then((dispose) => {
      if (disposed) dispose();
      else {
        cleanup = dispose;
        setState('ready');
      }
    }).catch(() => {
      if (!disposed) setState('error');
    });
    return () => {
      disposed = true;
      cleanup?.();
    };
  }, []);

  const confirmed = value?.pinConfirmed === true
    && value?.latitude != null && value?.longitude != null
    && String(value.latitude).trim() !== '' && String(value.longitude).trim() !== ''
    && Number.isFinite(Number(value?.latitude))
    && Number.isFinite(Number(value?.longitude));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
          Confirm map pin <span className="text-rose-500">*</span>
        </span>
        {confirmed ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 size={13} /> Confirmed
          </span>
        ) : null}
      </div>
      <div className="relative h-64 overflow-hidden rounded-xl border border-neutral-200 bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800">
        <div ref={containerRef} className="h-full w-full" aria-label="Address pin map" />
        {state === 'loading' ? (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 dark:bg-neutral-800">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-500" />
          </div>
        ) : null}
        {state === 'unavailable' || state === 'error' ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-5 text-center text-sm text-neutral-600 dark:text-neutral-300">
            <AlertCircle size={20} />
            The map is unavailable. Try again after the browser map configuration is restored.
          </div>
        ) : null}
      </div>
      <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400">
        <MapPin size={13} className="mt-0.5 shrink-0" />
        Click the exact entrance or drag the marker. Text fields do not confirm delivery coordinates.
      </p>
      {error ? <p className="text-[11px] text-rose-500">{error}</p> : null}
    </div>
  );
}
